import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { graphql, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import * as compose from 'lodash.flowright';
import * as Cookies from "es-cookie";
import StudentFormFields from './StudentFormFields';
import StudentFormSuccess from './StudentFormSuccess';
import './_admissionForms.scss';
import defaultLogo from "../../../images/notifylogo.png";

// ── GraphQL Queries & Mutations ──────
const GET_PUBLIC_FORM = gql`
  query ($form_id: Int!, $institute_id: Int) {
    getEnquiryFormFields(form_id: $form_id, institute_id: $institute_id) {
      id
      field_name
      field_key
      field_type_id
      field_type
      is_required
      options
      value_length
      order_no
    }
  }
`;

const SUBMIT_ENQUIRY_FORM = gql`
  mutation ($input: SubmitEnquiryFormInput!) {
    submitEnquiryForm(input: $input)
  }
`;

const CHECK_MOBILE = gql`
  query ($student_mobile: String!, $enquiry_form_id: Int!, $institute_id: Int) {
    checkEnquiryMobile(student_mobile: $student_mobile, enquiry_form_id: $enquiry_form_id, institute_id: $institute_id)
  }
`;

const GET_STREAMS = gql`
  query {
    getStreams {
      id
      stream
      classes
    }
  }
`;

// Classes that require a stream selection (e.g. Intermediate 1st/2nd year)
const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));
// ─────────────────────────────────────

class StudentFormPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitted: false,
      errorMsg: "",
      fields: [],
      baseFields: [],
      formData: {
        student_name: "",
        student_mobile: "",
        student_email: "",
        gender: "",
        dob: "",
        enquiry_course_id: "",
        stream_id: "",
        field_values: []
      },
      isDuplicateMobile: false,
      siblingUndertaking: false,
      website_url: "", // Honeypot field
      isCheckingMobile: false,
      isSubmitting: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.initFormFields();
  }

  componentDidUpdate(prevProps) {
    if (this.props.getPublicForm !== prevProps.getPublicForm) {
      this.initFormFields();
    }
  }

  // Resolve a field's master key (or null for custom fields), using field_key
  // first and falling back to the label for legacy rows without a key.
  resolveBaseKey = (f) => {
    const MASTER_KEYS = ["student_name", "student_mobile", "student_email", "dob", "gender", "enquiry_course_id", "stream_id"];
    if (f.field_key && MASTER_KEYS.includes(f.field_key)) return f.field_key;
    const label = (f.field_name || "").toLowerCase().trim().replace(/_/g, " ");
    if (["name", "full name", "student name"].includes(label)) return "student_name";
    if (["mobile", "mobile number", "student mobile", "primary contact number", "contact number", "phone number"].includes(label)) return "student_mobile";
    if (label.includes("email")) return "student_email";
    if (["date of birth", "dob"].includes(label)) return "dob";
    if (["gender", "sex"].includes(label)) return "gender";
    if (["enquiry class", "course name", "course"].includes(label)) return "enquiry_course_id";
    if (label === "stream") return "stream_id";
    return null;
  };

  initFormFields = () => {
    const { getPublicForm } = this.props;
    if (getPublicForm && getPublicForm.getEnquiryFormFields) {
      const allFields = getPublicForm.getEnquiryFormFields;

      const academicKws = ["school", "cgpa", "percentage", "marks", "ssc gpa", "state", "district", "city", "pincode", "zipcode", "zip code"];

      // Split into master (fixed) fields and custom fields; academic-only fields
      // are dropped. Both groups are ordered by order_no so the whole form follows
      // the saved arrangement (master fields always precede custom ones).
      const baseFields = [];
      const dynamic = [];
      allFields.forEach((f) => {
        const key = this.resolveBaseKey(f);
        if (key) {
          // Email is not shown by default on the form.
          if (key === "student_email") return;
          baseFields.push({ ...f, _baseKey: key });
          return;
        }
        const label = (f.field_name || "").toLowerCase().trim().replace(/_/g, " ");
        if (academicKws.some((kw) => label.includes(kw))) return;
        dynamic.push(f);
      });

      const byOrder = (a, b) => (a.order_no || 0) - (b.order_no || 0);
      baseFields.sort(byOrder);
      dynamic.sort(byOrder);

      // Dynamic labels/required flags for the fixed fields, keyed by master key.
      const baseLabels = {};
      const baseRequired = {};
      baseFields.forEach((f) => {
        baseLabels[f._baseKey] = f.field_name;
        baseRequired[f._baseKey] = !!f.is_required;
      });

      this.setState({
        baseFields,
        fields: dynamic,
        baseLabels,
        baseRequired
      });
    }
  };

  // Parse a field's options into [{label, value}] (handles array or "a, b, c").
  getOptions = (options) => {
    if (!options) return [];
    if (Array.isArray(options)) {
      return options.map((opt) => (typeof opt === "string" ? { label: opt, value: opt } : opt));
    }
    return options.toString().split(",").map((o) => ({ label: o.trim(), value: o.trim() })).filter((o) => o.label);
  };

  // The duplicate-mobile / sibling notice, shown right after the mobile field.
  renderDuplicateNotice = () => {
    const { isDuplicateMobile, siblingUndertaking } = this.state;
    if (!isDuplicateMobile) return null;
    return (
      <div className="af-duplicate-notice p-3 mb-4 rounded border" style={{ backgroundColor: '#fff7ed', borderColor: '#fed7aa', marginBottom: '20px' }}>
        <div className="d-flex align-items-center mb-2">
          <i className="fas fa-exclamation-triangle text-warning mr-2" style={{ fontSize: '18px' }} />
          <strong style={{ color: '#9a3412', fontSize: '15px' }}>Duplicate Mobile Number Detected</strong>
        </div>
        <p className="mb-3" style={{ fontSize: '14px', color: '#7c2d12', lineHeight: '1.4' }}>
          This mobile number is already registered in our system. If this is a different student (sibling), please confirm below.
        </p>
        <label className="d-flex align-items-center cursor-pointer mb-2" style={{ gap: '10px' }}>
          <input
            type="checkbox"
            checked={siblingUndertaking}
            onChange={(e) => this.setState({ siblingUndertaking: e.target.checked })}
            style={{ width: '18px', height: '18px' }}
          />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
            Is this a Sibling? I undertake that this is a separate entry for a sibling.
          </span>
        </label>
        {!siblingUndertaking && (
          <div className="mt-3 p-2 rounded bg-danger text-white text-center font-weight-bold" style={{ fontSize: '14px' }}>
            <i className="fas fa-ban mr-2" />
            Multiple entry detected. Registration terminated.
          </div>
        )}
      </div>
    );
  };

  // Render a single fixed (master) field by its key. Rendered in order_no order.
  renderBaseField = (f) => {
    const { formData } = this.state;
    const key = f._baseKey || this.resolveBaseKey(f);
    const label = f.field_name;
    const required = !!f.is_required;
    const star = required ? <span className="req">*</span> : null;

    switch (key) {
      case "student_name":
        return (
          <div className="form-group" key={f.id}>
            <label>{label} {star}</label>
            <input type="text" name="student_name" className="form-control" placeholder={`Enter ${label}`}
              maxLength={f.value_length || undefined}
              value={formData.student_name} onChange={this.handleChange} required={required} />
          </div>
        );
      case "student_mobile":
        return (
          <div className="form-group" key={f.id}>
            <label>{label} {star}</label>
            <input type="tel" name="student_mobile" className="form-control" placeholder="10 digit mobile"
              value={formData.student_mobile} onChange={this.handleChange} onBlur={this.handleMobileBlur}
              maxLength={f.value_length || 10} required={required} />
          </div>
        );
      case "student_email":
        return (
          <div className="form-group" key={f.id}>
            <label>{label} {star}</label>
            <input type="email" name="student_email" className="form-control" placeholder="Email address"
              maxLength={f.value_length || undefined}
              value={formData.student_email} onChange={this.handleChange} required={required} />
          </div>
        );
      case "dob":
        return (
          <div className="form-group" key={f.id}>
            <label>{label} {star}</label>
            <input type="date" name="dob" className="form-control"
              value={formData.dob} onChange={this.handleChange} required={required} />
          </div>
        );
      case "gender": {
        const opts = this.getOptions(f.options);
        const genderOpts = opts.length ? opts : [{ label: "Boy", value: "Boy" }, { label: "Girl", value: "Girl" }];
        return (
          <div className="form-group" key={f.id}>
            <label>{label} {star}</label>
            <select name="gender" className="form-control" value={formData.gender} onChange={this.handleChange} required={required}>
              <option value="">Select {label}</option>
              {genderOpts.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        );
      }
      case "enquiry_course_id": {
        const courseOpts = this.getOptions(f.options);
        const selectedCourse = courseOpts.find(c => String(c.value) === String(formData.enquiry_course_id));
        const showStream = selectedCourse && isStreamClass(selectedCourse.class_id);
        const allStreams = (this.props.getStreams && this.props.getStreams.getStreams) || [];
        const streamOptions = showStream
          ? allStreams.filter(st => (st.classes || "").split(",").map(c => c.trim()).includes(String(selectedCourse.class_id)))
          : [];
        return (
          <React.Fragment key={f.id}>
            <div className="form-group">
              <label>{label} {star}</label>
              <select name="enquiry_course_id" className="form-control" value={formData.enquiry_course_id} onChange={this.handleChange} required={required}>
                <option value="">Select {label}</option>
                {courseOpts.map((course, i) => (
                  <option key={i} value={course.value}>{course.label}</option>
                ))}
              </select>
            </div>
            {showStream && (
              <div className="form-group">
                <label>Stream <span className="req">*</span></label>
                <select name="stream_id" className="form-control" value={formData.stream_id} onChange={this.handleChange} required>
                  <option value="">Select Stream</option>
                  {streamOptions.map(st => (
                    <option key={st.id} value={st.id}>{st.stream}</option>
                  ))}
                </select>
              </div>
            )}
          </React.Fragment>
        );
      }
      // Stream renders with the course field above; suppress the standalone field.
      case "stream_id":
        return null;
      default:
        return null;
    }
  };

  // Helper to turn "engineering-admission-form" into "Engineering Admission Form"
  formatSlug = (slug) => {
    if (!slug || slug === "form") return "Enquiry Form";
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    if (name === "student_mobile") {
      filteredValue = value.replace(/[^0-9]/g, "");
    }

    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: filteredValue,
        // Changing the course invalidates any previously chosen stream
        ...(name === "enquiry_course_id" ? { stream_id: "" } : {})
      },
      // Reset duplicate state when mobile changes
      ...(name === "student_mobile" ? { isDuplicateMobile: false, siblingUndertaking: false } : {})
    }));
  };



  handleMobileBlur = async () => {
    const { formData } = this.state;
    const { form_id } = this.props.match.params;
    const queryParams = new URLSearchParams(window.location.search);
    const instId = queryParams.get("inst");

    if (formData.student_mobile.length === 10) {
      try {
        const { data } = await this.props.client.query({
          query: CHECK_MOBILE,
          variables: {
            student_mobile: formData.student_mobile,
            enquiry_form_id: parseInt(form_id) || 0,
            institute_id: instId ? parseInt(instId) : undefined,
          },
          fetchPolicy: "network-only",
        });

        if (data && data.checkEnquiryMobile) {
          this.setState({ isDuplicateMobile: true });
        } else {
          this.setState({ isDuplicateMobile: false, siblingUndertaking: false });
        }
      } catch (err) {
        console.error("Error checking mobile duplicate:", err);
      }
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ errorMsg: "" });

    const scrollToFirstError = () => {
      setTimeout(() => {
        const errorEl = document.querySelector('.is-invalid, .form-control:invalid, small.text-danger:not(.af-error-msg)');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    };

    const { formData, isDuplicateMobile, siblingUndertaking, website_url } = this.state;
    const { form_id } = this.props.match.params;
    const queryParams = new URLSearchParams(window.location.search);
    const instId = queryParams.get("inst");
    const branchId = queryParams.get("branch");

    // Basic Validation
    if (!formData.student_name.trim()) {
      this.setState({ errorMsg: "Name is required" });
      scrollToFirstError();
      return;
    }
    if (!formData.student_mobile.trim()) {
      this.setState({ errorMsg: "Mobile is required" });
      scrollToFirstError();
      return;
    }
    if (formData.student_mobile.length !== 10) {
      this.setState({ errorMsg: "Enter valid 10 digit mobile" });
      scrollToFirstError();
      return;
    }
    if (!formData.enquiry_course_id) {
      this.setState({ errorMsg: "Enquiry Class is required" });
      scrollToFirstError();
      return;
    }

    // If the chosen course maps to a stream class (15/16), a stream is required
    const courseField = (this.state.baseFields || []).find(f => f._baseKey === "enquiry_course_id");
    const selectedCourse = courseField
      ? this.getOptions(courseField.options).find(c => String(c.value) === String(formData.enquiry_course_id))
      : null;
    const courseNeedsStream = selectedCourse && isStreamClass(selectedCourse.class_id);
    if (courseNeedsStream && !formData.stream_id) {
      this.setState({ errorMsg: "Stream is required" });
      scrollToFirstError();
      return;
    }
    // Validation for DOB and Gender removed as they are no longer mandatory.

    // Antispam Honeypot check
    if (website_url) {
      console.warn("Bot detected via honeypot.");
      this.setState({ submitted: true });
      return;
    }

    if (isDuplicateMobile && !siblingUndertaking) {
      this.setState({ errorMsg: "Please confirm the Sibling Undertaking to proceed." });
      return;
    }

    try {
      this.setState({ isSubmitting: true });
      const remainingFormData = formData.field_values.map(fv => ({
        enquiry_field_id: fv.field_id,
        value: fv.field_value.toString()
      }));



      const source = queryParams.get("source") || "Direct";

      await this.props.submitEnquiryForm({
        variables: {
          input: {
            enquiry_form_id: parseInt(form_id) || 0,
            institute_id: instId ? (parseInt(instId) || 0) : 0,
            branch_id: branchId ? String(branchId) : "",
            student_name: formData.student_name.trim(),
            student_mobile: formData.student_mobile.trim(),
            student_email: formData.student_email.trim(),
            gender: formData.gender,
            dob: formData.dob,
            enquiry_course_id: parseInt(formData.enquiry_course_id) || 0,
            stream_id: courseNeedsStream && formData.stream_id ? parseInt(formData.stream_id) : null,
            inserted_by: 0,
            is_sibling: siblingUndertaking,
            remainingFormData,
            auto_assign_test: true,
            source_name: source
          }
        }
      });

      this.setState({ submitted: true, isSubmitting: false });
    } catch (err) {
      this.setState({ isSubmitting: false });
      const msg = err.message || "";
      if (msg.includes("already registered") || msg.includes("Duplicate") || msg.includes("already exists")) {
        this.setState({ isDuplicateMobile: true, errorMsg: "This mobile is already registered. If this is a sibling, check the box below and try again." });
      } else {
        this.setState({ errorMsg: msg.replace("GraphQL error: ", "") });
      }
    }
  };

  handleReset = () => {
    this.setState({
      formData: {
        student_name: "",
        student_mobile: "",
        student_email: "",
        gender: "",
        dob: "",
        enquiry_course_id: "",
        stream_id: "",
        field_values: [],

      },
      isDuplicateMobile: false,
      siblingUndertaking: false,
      errorMsg: "",
      submitted: false
    });
  };

  render() {
    const { getPublicForm } = this.props;
    const { form_slug } = this.props.match.params;
    const displayFormName = this.formatSlug(form_slug);
    // Institution logo from cookies, falling back to the bundled default.
    const formLogo = Cookies.get("url") || defaultLogo;

    if (getPublicForm.loading) {
      return (
        <div className="student-form-wrapper">
          <div className="student-form-card">
            <div className="form-header">
              <img src={formLogo} alt="logo" style={{ height: 90, width: "auto", maxWidth: "100%", objectFit: "contain" }} />
              <p>Loading form...</p>
            </div>
          </div>
        </div>
      );
    }

    if (getPublicForm.error) {
      return (
        <div className="student-form-wrapper">
          <div className="student-form-card">
            <div className="form-header">
              <img src={formLogo} alt="logo" style={{ height: 90, width: "auto", maxWidth: "100%", objectFit: "contain" }} />
              <div className="alert alert-danger">Error loading form. Please refresh.</div>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.submitted) {
      return <StudentFormSuccess name={this.state.formData.student_name} onReset={this.handleReset} />;
    }

    const { website_url, fields, errorMsg, baseFields = [] } = this.state;

    return (
      <div className="student-form-wrapper">
        <div className="student-form-card">
          <div className="form-header">
            <img src={formLogo} alt="logo" style={{ height: 90, width: "auto", maxWidth: "100%", objectFit: "contain" }} />
            <h2>{displayFormName}</h2>
            <p>Please fill in the details below</p>
          </div>

          <form onSubmit={this.handleSubmit} className="student-form">
            {/* Honeypot field (hidden from humans) */}
            <div style={{ display: 'none' }} aria-hidden="true">
              <input
                type="text"
                name="website_url"
                tabIndex="-1"
                value={website_url}
                onChange={e => this.setState({ website_url: e.target.value })}
                autoComplete="off"
              />
            </div>

            {/* Master + custom fields share one row so additional fields
                continue inline instead of starting a new line */}
            <div className="row">
              {baseFields.map((f) => (
                <div className="col-md-6 px-2" key={f.id}>
                  {this.renderBaseField(f)}
                </div>
              ))}

              {fields && fields.length > 0 && (
                <StudentFormFields
                  fields={fields}
                  onChange={(field_values) =>
                    this.setState(prevState => ({
                      formData: { ...prevState.formData, field_values }
                    }))
                  }
                />
              )}
            </div>

            {/* Duplicate-mobile / sibling notice (full width) */}
            {this.renderDuplicateNotice()}

            {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-block sf-submit-btn"
              disabled={this.state.isSubmitting}
            >
              {this.state.isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Submitting...
                </>
              ) : "Submit"}
            </button>
          </form>
        </div >
      </div >
    );
  }
}

export default withRouter(
  withApollo(
    compose(
      graphql(GET_PUBLIC_FORM, {
        options: (props) => {
          const queryParams = new URLSearchParams(window.location.search);
          return {
            variables: {
              form_id: parseInt(props.match.params.form_id) || 0,
              institute_id: queryParams.get("inst") ? parseInt(queryParams.get("inst")) : undefined,
            },
            fetchPolicy: "network-only",
          };
        },
        name: "getPublicForm",
      }),
      graphql(SUBMIT_ENQUIRY_FORM, {
        name: "submitEnquiryForm",
      }),
      graphql(GET_STREAMS, {
        name: "getStreams",
      }),
    )(StudentFormPage)
  )
);
